<script>
import TalkizyAPI from '@app/services/API/Talkizy';
import talkizyErrorMessage from '@app/utils/talkizyErrors';
import TiptapEditor from '@shared/components/TiptapEditor.vue';
import RemindersManager from '@app/components/RemindersManager.vue';
import isMeetingFinished, {
  MEETING_LEAD_MS,
  MEETING_LEAD_MINUTES,
  isMeetingLocked,
} from '@shared/utils/meeting';
import {
  DEFAULT_MEETING_TIMEZONE,
  timezoneSelectOptions,
  wallClockToISO,
  wallClockInstantMs,
  isoToWallClockDate,
} from '@shared/utils/meetingTimezone';
import moment from 'moment';

export default {
  name: 'ClasseVirtuelleMeetingParametres',
  components: { TiptapEditor, RemindersManager },
  inject: ['meetingStore'],
  data() {
    return {
      isSaving: false,
      formInitialized: false,
      now: Date.now(),
      lockTimer: null,
      // Plan Talkizy détaillé (GET /talkizy/plan) : porte les capacités du
      // forfait, dont can_get_feedback qui conditionne la satisfaction à chaud.
      talkizyPlanDetail: null,
      form: {
        title: '',
        startDate: null,
        startTime: '',
        timezone: DEFAULT_MEETING_TIMEZONE,
        duration: 60,
        accessType: 'OPENED',
        registrationType: null,
        satisfactionEnabled: false,
        invitationMessage: '',
        autoReminders: false,
        reminders: [],
      },
    };
  },
  computed: {
    meeting() {
      return this.meetingStore.meeting;
    },
    isLoading() {
      return this.meetingStore.isLoading;
    },
    isEnded() {
      return isMeetingFinished(this.meeting, this.now);
    },
    // Classe verrouillée : plus modifiable à moins de MEETING_LEAD_MINUTES du
    // début (classe imminente, en cours ou terminée).
    isLocked() {
      return isMeetingLocked(this.meeting, this.now);
    },
    // La satisfaction à chaud est une capacité de forfait (can_get_feedback,
    // config/plans.php côté Talkizy). Tant que le plan n'est pas connu, on ne
    // verrouille pas (le back reste le garde-fou) ; on ne bloque l'activation
    // que lorsqu'on SAIT que le forfait ne l'autorise pas.
    satisfactionAvailable() {
      const caps = this.talkizyPlanDetail && this.talkizyPlanDetail.capabilities;
      return !caps || !!caps.can_get_feedback;
    },
    // Quota d'émargement du forfait (max_signature) : nombre de classes sur
    // lesquelles l'émargement peut être activé (Basique = 3, PRO = 10). null =
    // illimité → aucune note affichée. L'émargement n'est pas un booléen de plan
    // (can_get_signature est vrai partout) : c'est ce plafond qui restreint.
    signatureLimit() {
      const caps = this.talkizyPlanDetail && this.talkizyPlanDetail.capabilities;
      const value = caps ? caps.max_signature : null;
      return (value === undefined || value === null) ? null : Number(value);
    },
    leadMinutes() {
      return MEETING_LEAD_MINUTES;
    },
    // Jour minimum sélectionnable = aujourd'hui (borné à minuit : le picker
    // compare des jours à minuit, une borne avec l'heure courante exclurait
    // aujourd'hui). La validité de l'horaire est contrôlée à l'enregistrement.
    minStartDate() {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d;
    },
    registrationOptions() {
      return [
        { value: null, label: 'Aucun', description: 'Pas d\'émargement requis.' },
        { value: 'signature', label: 'Signature', description: 'Signature en ligne (recommandé pour Qualiopi).' },
        { value: 'checkbox', label: 'Case à cocher', description: 'Le participant coche une case certifiant son identité.' },
        { value: 'otp', label: 'Code de vérification (OTP)', description: 'Code unique envoyé par e-mail à saisir avant d\'accéder à la visio.' },
      ];
    },
    satisfactionOptions() {
      return [
        { value: false, label: 'Désactivée', description: 'Aucune enquête de satisfaction n\'est proposée aux apprenants.' },
        { value: true, label: 'Activée', description: 'À la fin de la classe virtuelle, une note et un commentaire sont demandés aux apprenants.' },
      ];
    },
    timezoneOptions() {
      return timezoneSelectOptions(this.form.timezone, this.startsAt);
    },
    // Recombine les deux champs (date + heure) en un seul Date dont la roue
    // locale du navigateur porte la date/heure saisie, tel qu'attendu par les
    // helpers de fuseau (wallClockToISO / wallClockInstantMs).
    startsAt() {
      if (!this.form.startDate || !this.form.startTime) return null;
      const [hours, minutes] = this.form.startTime.split(':').map(Number);
      const date = new Date(this.form.startDate);
      date.setHours(hours, minutes, 0, 0);
      return date;
    },
  },
  watch: {
    meeting: {
      immediate: true,
      handler(m) {
        if (!m) {
          return;
        }
        if (!this.formInitialized) {
          const tz = m.timezone || DEFAULT_MEETING_TIMEZONE;
          // Les champs affichent l'heure DANS le fuseau de la classe (pas celui
          // du navigateur) : on convertit l'instant absolu en roue locale du
          // fuseau, puis on l'éclate en date + heure ("HH:mm").
          const wall = m.starts_at ? isoToWallClockDate(m.starts_at, tz) : null;
          this.form = {
            title: m.title,
            startDate: wall,
            startTime: wall ? moment(wall).format('HH:mm') : '',
            timezone: tz,
            duration: m.duration || 60,
            accessType: m.access_type || 'OPENED',
            registrationType: m.registration_type || null,
            satisfactionEnabled: !!m.satisfaction_enabled,
            invitationMessage: m.participant_invitation_message || '',
            autoReminders: !!(m.reminders && m.reminders.enabled),
            reminders: m.reminders && m.reminders.offsets ? [...m.reminders.offsets] : [],
          };
          this.formInitialized = true;
        } else {
          // Keep the title in sync when it is renamed from the header,
          // without discarding unsaved edits to the other fields.
          this.form.title = m.title;
        }
      },
    },
  },
  mounted() {
    // Rafraîchit l'état de verrouillage au fil du temps (le seuil des 10 min
    // peut être franchi pendant que la page est ouverte).
    this.lockTimer = setInterval(() => { this.now = Date.now(); }, 15000);
    this.fetchPlan();
  },
  beforeDestroy() {
    if (this.lockTimer) clearInterval(this.lockTimer);
  },
  methods: {
    // Capacités du forfait Talkizy (dont can_get_feedback) : même source que
    // l'écran Paramètres global. En cas d'échec, talkizyPlanDetail reste null →
    // on ne verrouille pas côté front, le back reste le garde-fou.
    async fetchPlan() {
      try {
        this.talkizyPlanDetail = await TalkizyAPI.getPlan();
      } catch (e) {
        this.talkizyPlanDetail = null;
      }
    },
    // Un input[type=time] natif n'ouvre son sélecteur que via l'icône horloge ;
    // un clic sur les chiffres ne fait que sélectionner le segment. showPicker()
    // ouvre le sélecteur quel que soit l'endroit cliqué (no-op si non supporté).
    openTimePicker(event) {
      const input = event.target.tagName === 'INPUT'
        ? event.target
        : event.currentTarget.querySelector('input');

      if (input && typeof input.showPicker === 'function') {
        try {
          input.showPicker();
        } catch (e) {
          // showPicker peut refuser (geste utilisateur requis, iframe…) : la
          // saisie clavier reste possible.
        }
      }
    },
    buildPayload(overrides = {}) {
      return {
        title: this.form.title,
        starts_at: wallClockToISO(this.startsAt, this.form.timezone),
        duration: Number(this.form.duration),
        timezone: this.form.timezone,
        access_type: this.form.accessType,
        registration_type: this.form.registrationType,
        satisfaction_enabled: this.form.satisfactionEnabled,
        participant_invitation_message: this.form.invitationMessage,
        auto_reminders_enabled: this.form.autoReminders,
        reminder_offsets: this.form.autoReminders ? this.form.reminders.map(Number) : [],
        ...overrides,
      };
    },
    scheduleChanged() {
      const current = this.meetingStore.meeting && this.meetingStore.meeting.starts_at;
      if (!current || !this.startsAt) return false;
      // Compare les instants absolus (une modif du fuseau seule peut déplacer
      // l'heure réelle et doit donc notifier les apprenants).
      const next = wallClockToISO(this.startsAt, this.form.timezone);
      return new Date(current).getTime() !== new Date(next).getTime();
    },
    save() {
      // Une classe imminente (< délai), en cours ou terminée n'est plus modifiable.
      if (this.isLocked) {
        this.$buefy.toast.open({
          message: `La classe virtuelle ne peut plus être modifiée à moins de ${MEETING_LEAD_MINUTES} minutes de son début.`,
          type: 'is-warning',
        });
        return;
      }
      if (!this.form.title || !this.startsAt || !this.form.duration) {
        this.$buefy.toast.open({ message: 'Titre, date, heure et durée sont requis.', type: 'is-warning' });
        return;
      }
      // Même règle qu'à la création : si l'horaire est modifié, la nouvelle date
      // doit rester au moins MEETING_LEAD_MINUTES dans le futur.
      if (this.scheduleChanged()
        && wallClockInstantMs(this.startsAt, this.form.timezone) < Date.now() + MEETING_LEAD_MS) {
        this.$buefy.toast.open({
          message: `La classe virtuelle doit être programmée au minimum ${MEETING_LEAD_MINUTES} minutes après l’heure actuelle.`,
          type: 'is-warning',
        });
        return;
      }
      if (this.scheduleChanged()) {
        this.$buefy.dialog.confirm({
          title: 'Notification aux apprenants',
          message: "Vous avez modifié la date ou l'heure de la classe virtuelle. "
            + 'Un email de notification sera envoyé aux apprenants invités. Voulez-vous continuer ?',
          confirmText: 'Confirmer',
          cancelText: 'Annuler',
          onConfirm: () => this.doSave(),
        });
        return;
      }
      this.doSave();
    },
    async doSave() {
      this.isSaving = true;
      try {
        this.meetingStore.meeting = await TalkizyAPI.updateMeeting(
          this.$route.params.meetingId,
          this.buildPayload(),
        );
        this.$buefy.toast.open({ message: 'Enregistré !', type: 'is-success' });
      } catch (e) {
        const message = talkizyErrorMessage(e, 'La mise à jour a échoué.');
        this.$showMessage.error(message);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <b-skeleton v-if="isLoading" height="120" :count="2" :animated="true" />

    <div
      v-else-if="!meeting"
      class="box has-text-centered has-text-grey"
    >
      Impossible de charger la visio.
    </div>

    <template v-else>
      <div
        v-if="isEnded"
        class="box is-info content mb-4"
      >
        <p>
          <b-icon icon="lock" class="has-text-black mr-3" />
          <strong>Cette classe virtuelle est terminée</strong> :
          ses paramètres sont en lecture seule.
        </p>
      </div>

      <div
        v-else-if="isLocked"
        class="box is-info content mb-4"
      >
        <p>
          <b-icon icon="lock" class="has-text-black mr-3" />
          <strong>Cette classe virtuelle démarre dans moins de {{ leadMinutes }} minutes ou est en cours</strong> :
          ses paramètres ne sont plus modifiables.
        </p>
      </div>

      <AppToggleSection :is-open="true">
          <template #title>
            Général
          </template>
          <template #default>
            <AppFeatureWrapper>
              <template #content>
                <p>
                  Modifiez les informations principales de votre visio comme le titre,
                  la date, l'heure ou encore la durée prévisionnelle.
                </p>
                <p>
                  La durée prévisionnelle est donnée à titre indicatif : une classe virtuelle déjà
                  démarrée n'est pas interrompue automatiquement si elle se prolonge,
                  sauf si vous atteignez la durée maximum de votre abonnement.
                </p>
                <p>
                  Si vous modifiez la date ou l'heure, vos apprenants recevront un email
                  les notifiant de la modification.
                </p>
              </template>
              <template #form>
                <div class="box">
                  <b-field label="Titre">
                    <b-input
                      v-model="form.title"
                      placeholder="Ex. Cours du lundi"
                      maxlength="255"
                      :has-counter="false"
                      required
                      :disabled="isLocked"
                    />
                  </b-field>

                  <div class="columns">
                    <div class="column is-4">
                      <b-field label="Date">
                        <b-datepicker
                          v-model="form.startDate"
                          placeholder="Sélectionner..."
                          icon="calendar"
                          :first-day-of-week="1"
                          :min-date="minStartDate"
                          :disabled="isLocked"
                        />
                      </b-field>
                    </div>
                    <div class="column is-3">
                      <b-field label="Heure de début" label-for="meeting_start_time">
                        <b-input
                          id="meeting_start_time"
                          v-model="form.startTime"
                          type="time"
                          icon="clock"
                          expanded
                          :disabled="isLocked"
                          @click.native="openTimePicker"
                        />
                      </b-field>
                    </div>
                    <div class="column is-5">
                      <b-field label="Fuseau horaire">
                        <b-select v-model="form.timezone" expanded :disabled="isLocked">
                          <option
                            v-for="tz in timezoneOptions"
                            :key="tz.value"
                            :value="tz.value"
                          >
                            {{ tz.label }}
                          </option>
                        </b-select>
                      </b-field>
                    </div>
                  </div>

                  <b-field label="Durée prévisionnelle (minutes)">
                    <b-input v-model="form.duration" type="number" min="1" max="480" :disabled="isLocked" />
                  </b-field>

                  <b-field v-if="!isLocked" class="has-text-right">
                    <b-button type="is-primary" :loading="isSaving" @click="save">
                      Mettre à jour
                    </b-button>
                  </b-field>
                </div>
              </template>
            </AppFeatureWrapper>
          </template>
        </AppToggleSection>

        <AppToggleSection>
          <template #title>
            Accès
          </template>
          <template #default>
            <AppFeatureWrapper>
              <template #content>
                <p>
                  Choisissez comment vos participants accèdent à la classe virtuelle.
                </p>
                <p>
                  En mode <strong>Ouvert</strong>, les participants rejoignent
                  directement la classe virtuelle, sans salle d'attente.
                </p>
                <p>
                  En mode <strong>Fermé</strong>, les participants patientent dans une
                  salle d'attente et vous validez manuellement chaque entrée. Idéal pour
                  contrôler qui accède à la classe virtuelle.
                </p>
              </template>
              <template #form>
                <div class="box">
                  <b-field label="Type d'accès">
                    <b-select v-model="form.accessType" expanded :disabled="isLocked">
                      <option value="OPENED">
                        Ouvert (sans salle d'attente)
                      </option>
                      <option value="CLOSED">
                        Fermé (avec salle d'attente)
                      </option>
                    </b-select>
                  </b-field>

                  <b-field v-if="!isLocked" class="has-text-right">
                    <b-button type="is-primary" :loading="isSaving" @click="save">
                      Mettre à jour
                    </b-button>
                  </b-field>
                </div>
              </template>
            </AppFeatureWrapper>
          </template>
        </AppToggleSection>

        <AppToggleSection>
          <template #title>
            Message d'invitation
          </template>
          <template #default>
            <AppFeatureWrapper>
              <template #content>
                <p>
                  Personnalisez le contenu de l'email d'invitation envoyé à vos
                  participants pour les inviter à rejoindre la classe virtuelle.
                </p>
                <p>
                  Profitez-en pour préciser tout ce qui les aidera à bien s'y préparer :
                  le programme, le déroulé de la session ou encore les éventuels prérequis.
                </p>
              </template>
              <template #form>
                <div class="box">
                  <b-field label="Message d'invitation">
                    <TiptapEditor
                      v-if="!isLocked"
                      :value="form.invitationMessage"
                      :hidden="['h2']"
                      @input="form.invitationMessage = $event"
                    />
                    <div
                      v-else
                      class="content"
                      v-html="form.invitationMessage || '—'"
                    />
                  </b-field>

                  <b-field v-if="!isLocked" class="has-text-right">
                    <b-button type="is-primary" :loading="isSaving" @click="save">
                      Mettre à jour
                    </b-button>
                  </b-field>
                </div>
              </template>
            </AppFeatureWrapper>
          </template>
        </AppToggleSection>

        <AppToggleSection>
          <template #title>
            Type d'émargement
          </template>
          <template #default>
            <AppFeatureWrapper>
              <template #content>
                <p>
                  L'émargement permet aux participants d'attester leur présence à la
                  classe virtuelle et de conserver une preuve exploitable, notamment dans le cadre
                  d'une certification Qualiopi.
                </p>
                <p>
                  Choisissez le niveau de d'émargement adapté : signature en ligne,
                  simple case à cocher, ou code de vérification envoyé par e-mail.
                  Sélectionnez <strong>Aucun</strong> si vous n'avez pas besoin d'émargement.
                </p>
                <p>
                  L'émargement par signature constitue une preuve fiable, <strong>conforme au règlement européen eIDAS
                  (signature électronique simple)</strong>,
                  grâce à l'horodatage et le scellement des données.
                </p>
                <p>
                  Activer un émargement vous oblige également, en tant qu'animateur,
                  à signer pour pouvoir démarrer la classe virtuelle.
                </p>
              </template>
              <template #form>
                <div class="box">
                  <label
                    v-for="option in registrationOptions"
                    :key="option.value === null ? 'none' : option.value"
                    class="cvmparams_radio"
                    :class="{ 'cvmparams_radio--active': form.registrationType === option.value }"
                  >
                    <b-radio v-model="form.registrationType" :native-value="option.value" :disabled="isLocked" />
                    <span class="cvmparams_radio_text">
                      <strong>{{ option.label }}</strong>
                      <span class="is-block is-size-8 has-text-grey-dark">{{ option.description }}</span>
                    </span>
                  </label>

                  <b-notification
                    v-if="signatureLimit !== null"
                    type="is-warning is-light"
                    :closable="false"
                    class="is-size-8 mt-2"
                  >
                    <b-icon icon="info-circle" size="is-small" class="mr-2" />
                    Votre forfait Talkizy permet d’activer l’émargement sur
                    {{ signatureLimit }} classe{{ signatureLimit > 1 ? 's' : '' }}
                    virtuelle{{ signatureLimit > 1 ? 's' : '' }} au total.
                    Pour aller au-delà, passez à un forfait supérieur.
                  </b-notification>

                  <b-field v-if="!isLocked" class="has-text-right mt-4">
                    <b-button type="is-primary" :loading="isSaving" @click="save">
                      Mettre à jour
                    </b-button>
                  </b-field>
                </div>
              </template>
            </AppFeatureWrapper>
          </template>
        </AppToggleSection>

        <AppToggleSection>
          <template #title>
            Satisfaction
          </template>
          <template #default>
            <AppFeatureWrapper>
              <template #content>
                <p>
                  Recueillez la satisfaction à chaud de vos apprenants : à la fin
                  de la classe virtuelle, une note et un commentaire leur sont demandés.
                </p>
                <p>
                  Retrouvez ensuite les réponses dans l'onglet Apprenants de la
                  classe virtuelle, ainsi que sur la fiche de chaque apprenant.
                </p>
              </template>
              <template #form>
                <div class="box">
                  <label
                    v-for="option in satisfactionOptions"
                    :key="String(option.value)"
                    class="cvmparams_radio"
                    :class="{
                      'cvmparams_radio--active': form.satisfactionEnabled === option.value,
                      'cvmparams_radio--locked': option.value === true && !satisfactionAvailable,
                    }"
                  >
                    <b-radio
                      v-model="form.satisfactionEnabled"
                      :native-value="option.value"
                      :disabled="isLocked || (option.value === true && !satisfactionAvailable)"
                    />
                    <span class="cvmparams_radio_text">
                      <strong>
                        {{ option.label }}
                        <span
                          v-if="option.value === true && !satisfactionAvailable"
                          class="tag is-warning ml-2"
                        >
                          <b-icon icon="lock" size="is-small" class="mr-1" />
                          Forfait supérieur
                        </span>
                      </strong>
                      <span class="is-block is-size-8 has-text-grey-dark">{{ option.description }}</span>
                    </span>
                  </label>

                  <b-notification
                    v-if="!satisfactionAvailable"
                    type="is-warning is-light"
                    :closable="false"
                    class="is-size-8 mt-2"
                  >
                    <b-icon icon="lock" size="is-small" class="mr-2" />
                    L'enquête de satisfaction à chaud n'est pas incluse dans votre forfait Talkizy
                    actuel. Passez à un forfait supérieur sur Talkizy pour l'activer.
                  </b-notification>

                  <b-field v-if="!isLocked" class="has-text-right mt-4">
                    <b-button type="is-primary" :loading="isSaving" @click="save">
                      Mettre à jour
                    </b-button>
                  </b-field>
                </div>
              </template>
            </AppFeatureWrapper>
          </template>
        </AppToggleSection>

        <AppToggleSection>
          <template #title>
            Rappels automatiques
          </template>
          <template #default>
            <AppFeatureWrapper>
              <template #content>
                <p>
                  Envoyez automatiquement un email de rappel à vos apprenants
                  avant le début de la classe virtuelle, pour limiter les oublis
                  et les absences.
                </p>
                <p>
                  Vous pouvez programmer plusieurs rappels à des moments différents,
                  par exemple un jour avant puis une heure avant le démarrage.
                </p>
              </template>
              <template #form>
                <div class="box">
                  <RemindersManager
                    :enabled.sync="form.autoReminders"
                    :reminders.sync="form.reminders"
                    :disabled="isLocked"
                  />

                  <b-field v-if="!isLocked" class="has-text-right mt-4">
                    <b-button type="is-primary" :loading="isSaving" @click="save">
                      Mettre à jour
                    </b-button>
                  </b-field>
                </div>
              </template>
            </AppFeatureWrapper>
          </template>
        </AppToggleSection>

    </template>
  </div>
</template>

<style lang="scss" scoped>
.cvmparams_radio {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  border: 1px solid $theme_color_grey_light;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  cursor: pointer;

  &--active {
    border-color: $theme_color_primary;
    background: $theme_color_primary_light;
  }

  // Option indisponible sur le forfait courant : grisée et non cliquable, pour
  // que l'utilisateur voie clairement qu'elle est verrouillée (le tag « Forfait
  // supérieur » et la notification en dessous expliquent pourquoi).
  &--locked {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &_text {
    display: flex;
    flex-direction: column;
  }
}

// Le clic ouvre le sélecteur natif (showPicker) : le curseur main signale que
// tout le champ heure est cliquable, comme le champ heure du calendrier.
::v-deep input[type='time'] {
  cursor: pointer;
}
</style>
